import { useState } from 'react';
import { useT } from '@/store';
import { Modal } from '@/components/ui/Overlay';
import { ErrorState, SkeletonRows, EmptyState } from '@/components/ui/Panel';
import { GPS_REPORT_PERIODS, useGpsReport } from '@/hooks/useGpsReport';
import { ANALYTICS_COLORS } from '@/hooks/useAnalytics';
import { formatKm, groupDigits, niceMax } from '@/lib/format';

const CLASS_TONE = {
  own_quarry: 'text-green',
  competitor_quarry: 'text-amber',
  client_site: 'text-accent',
  depot: 'text-dim',
  other: 'text-dim',
};

/**
 * Where a vehicle actually spent its time, per core/gps_report.py: own-
 * quarry visits, competitor-quarry visits, and everything else, clustered
 * from bulk-imported telematics history (vehicle_gps_pings). Opened by
 * clicking a vehicle's plate on the Transportas grid — see VehicleGrid.jsx.
 *
 * Same period-picker convention as AnalyticsView.jsx (?days=), so day /
 * week / month / 6mo / 12mo reads as one consistent pattern across both
 * report screens rather than a bespoke control just for this one.
 */
export function VehicleGpsReportModal({ open, onClose, vehicle }) {
  const t = useT();
  const [days, setDays] = useState(30);
  const { data, isLoading, isError, error } = useGpsReport(vehicle?.vehicle_id, days);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={vehicle ? t('gpsReport.title', { plate: vehicle.license_plate }) : ''}
      width="max-w-[820px]"
      footer={
        <button type="button" className="btn" onClick={onClose}>
          {t('common.closeBtn')}
        </button>
      }
    >
      <div className="space-y-4">
        <div className="flex gap-1.5">
          {GPS_REPORT_PERIODS.map((period) => (
            <button
              key={period}
              type="button"
              className={`btn ${days === period ? 'btn-primary' : ''}`}
              onClick={() => setDays(period)}
            >
              {t(`gpsReport.period${period}`)}
            </button>
          ))}
        </div>

        {isError ? <ErrorState error={error} /> : null}
        {isLoading ? <SkeletonRows rows={6} /> : null}

        {!isLoading && data?.insufficient_data ? (
          <EmptyState>{t('gpsReport.noData')}</EmptyState>
        ) : null}

        {!isLoading && data && !data.insufficient_data ? (
          <>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {/* V-352. formatKm(), not raw interpolation: this was the ONE km
                  figure in the console printed without it, and it is two
                  lines above the leakage estimate on the same panel. The
                  backend sends round(total_km, 1), so raw it renders
                  "80000.5 km" — an English decimal point and no grouping —
                  while VehicleGrid shows the same truck's odometer as
                  "402 000 km" one screen away. That is the exact "two
                  components rendered the same odometer two ways" formatKm()
                  was written for. Losing the tenth is correct rather than
                  incidental: this is a straight-line estimate between GPS
                  fixes, and a tenth of a kilometre on an 80 000 km figure is
                  the same false precision fmtEur() below rounds away. */}
              <Stat
                label={t('gpsReport.totalKm')}
                value={formatKm(data.summary.total_km_estimated)}
              />
              <Stat
                label={t('gpsReport.workDays')}
                value={`${data.summary.work_days} / ${data.summary.days_with_data}`}
                sub={
                  data.summary.days_with_data < data.summary.period_days
                    ? t('gpsReport.daysWithDataNote', {
                        days: data.summary.days_with_data,
                        period: data.summary.period_days,
                      })
                    : null
                }
              />
              <Stat label={t('gpsReport.distinctSites')} value={data.summary.distinct_sites} />
              <Stat
                label={t('gpsReport.ownQuarry')}
                value={fmtMin(data.summary.own_quarry_minutes)}
                sub={t('gpsReport.visitsN', { n: data.summary.own_quarry_visits })}
                tone="text-green"
              />
              <Stat
                label={t('gpsReport.competitorQuarry')}
                value={fmtMin(data.summary.competitor_quarry_minutes)}
                sub={t('gpsReport.visitsN', { n: data.summary.competitor_quarry_visits })}
                tone="text-amber"
              />
              <Stat label={t('gpsReport.depotTime')} value={fmtMin(data.summary.depot_minutes)} tone="text-dim" />
              {data.summary.estimated_leakage_eur != null ? (
                <Stat
                  label={t('gpsReport.estimatedLeakage')}
                  value={fmtEur(data.summary.estimated_leakage_eur)}
                  tone="text-amber"
                  title={t('gpsReport.estimatedLeakageHint')}
                />
              ) : null}
              {worthCheckingDays(data.summary) != null ? (
                <Stat
                  label={t('gpsReport.worthChecking')}
                  value={worthCheckingDays(data.summary)}
                  sub={t('gpsReport.worthCheckingSub')}
                  tone={worthCheckingDays(data.summary) > 0 ? 'text-red' : 'text-green'}
                />
              ) : null}
            </div>
            <p className="text-[10.5px] text-dim">{t('gpsReport.estimateNote')}</p>
            {data.summary.estimated_leakage_eur != null ? (
              <p className="text-[10.5px] text-amber">{t('gpsReport.estimatedLeakageHint')}</p>
            ) : null}

            <DailyActivityChart daily={data.daily} t={t} />

            <SitesTable sites={data.sites} t={t} />
          </>
        ) : null}
      </div>
    </Modal>
  );
}

const CHART_W = 760;
const CHART_H = 190;
const CHART_M = { top: 10, right: 8, bottom: 20, left: 32 };

/**
 * Km per day, from core/gps_report.py's `daily` breakdown — same
 * hand-drawn-SVG convention as ProfitabilityChart.jsx (one chart, fixed
 * shape, not worth a charting library). Weekend columns get a faint wash
 * so a week's rhythm reads at a glance; days without pings are just zero
 * bars, not gaps, since "the truck did not move" is real information here.
 */
function DailyActivityChart({ daily, t }) {
  const [hover, setHover] = useState(null);
  if (!daily || daily.length === 0) return null;

  const n = daily.length;
  const plotW = CHART_W - CHART_M.left - CHART_M.right;
  const plotH = CHART_H - CHART_M.top - CHART_M.bottom;
  const slot = plotW / n;
  const barW = Math.max(slot * 0.62, 1);
  const maxKm = niceMax(Math.max(1, ...daily.map((d) => d.km)));
  const yFor = (km) => CHART_M.top + plotH - (Math.max(km, 0) / maxKm) * plotH;
  const xFor = (i) => CHART_M.left + i * slot;
  const labelEvery = Math.ceil(n / 10 || 1);

  return (
    <div>
      <h4 className="mb-1 text-[10.5px] font-bold uppercase tracking-[0.03em] text-dim">
        {t('gpsReport.dailyActivity')}
      </h4>
      <p className="mb-2 text-[10.5px] text-dim">{t('gpsReport.dailyActivitySub')}</p>
      <div className="relative overflow-x-auto">
        <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} width="100%" height={CHART_H} role="img">
          {[0, 0.5, 1].map((frac) => {
            const y = CHART_M.top + plotH - frac * plotH;
            return (
              <g key={frac}>
                <line
                  x1={CHART_M.left}
                  y1={y}
                  x2={CHART_W - CHART_M.right}
                  y2={y}
                  stroke="#262B36"
                  strokeWidth="1"
                />
                <text x={CHART_M.left - 6} y={y + 3} textAnchor="end" fontSize="9" fill="#8A90A0">
                  {Math.round(frac * maxKm)}
                </text>
              </g>
            );
          })}

          {daily.map((d, i) => {
            const dow = new Date(`${d.date}T12:00:00`).getDay();
            return dow === 0 || dow === 6 ? (
              <rect
                key={`we-${d.date}`}
                x={xFor(i)}
                y={CHART_M.top}
                width={slot}
                height={plotH}
                fill="#FFFFFF"
                opacity={0.03}
              />
            ) : null;
          })}

          {daily.map((d, i) => (
            <rect
              key={`bar-${d.date}`}
              x={xFor(i) + (slot - barW) / 2}
              y={yFor(d.km)}
              width={barW}
              height={Math.max(CHART_M.top + plotH - yFor(d.km), d.km > 0 ? 1 : 0)}
              rx="1.5"
              fill={ANALYTICS_COLORS.revenue}
              opacity={hover && hover.date !== d.date ? 0.4 : 0.85}
            />
          ))}

          {daily.map((d, i) => (
            <rect
              key={`hit-${d.date}`}
              x={xFor(i)}
              y={CHART_M.top}
              width={slot}
              height={plotH}
              fill="transparent"
              onMouseEnter={() => setHover(d)}
              onMouseLeave={() => setHover(null)}
            />
          ))}

          {daily.map((d, i) =>
            i % labelEvery === 0 ? (
              <text
                key={`x-${d.date}`}
                x={xFor(i) + slot / 2}
                y={CHART_H - 6}
                textAnchor="middle"
                fontSize="9"
                fill="#8A90A0"
              >
                {d.date.slice(8)}.{d.date.slice(5, 7)}
              </text>
            ) : null,
          )}
        </svg>

        {hover ? (
          <div className="pointer-events-none absolute right-2 top-2 rounded-[9px] border border-line bg-panel-2 px-2.5 py-2 text-[11px] shadow-lg">
            <div className="mb-1 font-semibold">{hover.date}</div>
            <div className="flex justify-between gap-4">
              <span className="text-dim">{t('gpsReport.dailyTooltipKm', { km: hover.km })}</span>
            </div>
            <div className="flex justify-between gap-4 text-dim">
              {t('gpsReport.dailyTooltipDriving', { hours: (hover.driving_minutes / 60).toFixed(1) })}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function fmtMin(minutes) {
  if (minutes >= 60) return `${(minutes / 60).toFixed(1)} val`;
  return `${Math.round(minutes)} min`;
}

/** Rounded to the nearest 10 EUR above €100 — this is a rough proxy (see
 *  core/gps_report.py's estimate_competitor_leakage_eur()), so a false
 *  sense of precision (e.g. "€1 247") would undercut the "estimate" framing
 *  the ≈ prefix and estimatedLeakageHint caption are already carrying.
 *
 *  V-352. Grouped with lib/format.js's groupDigits(), not
 *  `toLocaleString('en-US')`: that put a COMMA between the groups, and in
 *  Lithuanian a comma is the DECIMAL separator — so a €14 400 estimate
 *  rendered "≈€14,400" and reads as fourteen euros forty to the dispatcher
 *  eurExact() is printing "€14 400,00" for two panels away. Under €1000 the
 *  two spellings agree, which is why every fixture here missed it. */
function fmtEur(eur) {
  const rounded = eur >= 100 ? Math.round(eur / 10) * 10 : Math.round(eur);
  return `≈€${groupDigits(rounded)}`;
}

/** Only present once orders exist to match against (see
 *  blueprints/reporting.py's compute_vehicle_gps_report() — the
 *  unmatched_*_days fields are only added when there's a quarry/site to
 *  cross-reference at all). null, not 0, when there's nothing to check
 *  yet, so the stat can stay hidden rather than claiming a clean answer. */
function worthCheckingDays(summary) {
  if (
    summary.unmatched_own_quarry_days == null &&
    summary.unmatched_competitor_quarry_days == null &&
    summary.unmatched_client_site_days == null
  ) {
    return null;
  }
  return (
    (summary.unmatched_own_quarry_days ?? 0) +
    (summary.unmatched_competitor_quarry_days ?? 0) +
    (summary.unmatched_client_site_days ?? 0)
  );
}

const CLASS_LABEL_KEY = {
  own_quarry: 'gpsReport.class.own_quarry',
  competitor_quarry: 'gpsReport.class.competitor_quarry',
  client_site: 'gpsReport.class.client_site',
  depot: 'gpsReport.class.depot',
  other: 'gpsReport.class.other',
};

/** Sorted by dwell time server-side already (core/gps_report.py) — shown
 *  as-is, capped here rather than paginated, since a report modal is a
 *  quick look, not a full data-management surface. */
function SitesTable({ sites, t }) {
  if (sites.length === 0) return null;
  const shown = sites.slice(0, 20);
  return (
    <div>
      <h4 className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.03em] text-dim">
        {t('gpsReport.sitesHeading')}
      </h4>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-[11px]">
          <thead>
            <tr className="text-dim">
              <th className="px-2 py-1 font-semibold">{t('gpsReport.col.location')}</th>
              <th className="px-2 py-1 font-semibold">{t('gpsReport.col.type')}</th>
              <th className="px-2 py-1 font-semibold">{t('gpsReport.col.visits')}</th>
              <th className="px-2 py-1 font-semibold">{t('gpsReport.col.dwell')}</th>
              <th className="px-2 py-1 font-semibold">{t('gpsReport.col.orders')}</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((s, i) => (
              <tr key={`${s.lat}-${s.lng}-${i}`} className="border-t border-line">
                <td className="px-2 py-1">
                  <div className="font-semibold text-ink">
                    {s.classification === 'depot'
                      ? t('gpsReport.depotLabel')
                      : s.nearest_quarry_name && s.nearest_quarry_distance_km <= 0.5
                        ? s.nearest_quarry_name
                        : `${s.lat.toFixed(4)}, ${s.lng.toFixed(4)}`}
                  </div>
                  {s.classification === 'other' && s.nearest_quarry_name ? (
                    <div className="text-[10px] text-dim">
                      {t('gpsReport.nearestHint', { name: s.nearest_quarry_name, km: s.nearest_quarry_distance_km })}
                    </div>
                  ) : null}
                </td>
                <td className={`px-2 py-1 font-semibold ${CLASS_TONE[s.classification]}`}>
                  {t(CLASS_LABEL_KEY[s.classification])}
                </td>
                <td className="px-2 py-1">
                  {s.visits} / {s.days}d
                </td>
                <td className="px-2 py-1">{fmtMin(s.total_dwell_minutes)}</td>
                <td className="px-2 py-1">
                  {s.unmatched_dates ? <OrderMatchCell site={s} t={t} /> : <span className="text-dim">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sites.length > shown.length ? (
        <p className="mt-2 text-[10.5px] text-dim">
          {t('gpsReport.moreSites', { n: sites.length - shown.length })}
        </p>
      ) : null}
    </div>
  );
}

/** Own/competitor/client-site rows only — how many of this site's visit
 *  days have a matching order (core/gps_report.py's
 *  match_visits_to_orders()). A visit-day with no matching order isn't a
 *  confirmed problem (refueling, maintenance, a driver detour) — it's
 *  flagged as worth a second look, never stated as a fact, which is why
 *  this reads "3/5 su užsakymu" rather than something that sounds like an
 *  accusation. */
function OrderMatchCell({ site, t }) {
  const total = site.matched_dates.length + site.unmatched_dates.length;
  if (total === 0) return <span className="text-dim">—</span>;
  if (site.unmatched_dates.length === 0) {
    return <span className="text-green">{t('gpsReport.allMatched', { n: total })}</span>;
  }
  return (
    <span className="text-amber" title={site.unmatched_dates.join(', ')}>
      {t('gpsReport.someUnmatched', { unmatched: site.unmatched_dates.length, total })}
    </span>
  );
}

function Stat({ label, value, sub, tone = 'text-ink', title }) {
  return (
    <div className="rounded-[10px] border border-line bg-white/2 px-3 py-2" title={title}>
      <div className={`text-[16px] font-bold ${tone}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-[0.03em] text-dim">{label}</div>
      {sub ? <div className="text-[10px] text-dim">{sub}</div> : null}
    </div>
  );
}
